import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_apis: number
  active_apis: number
  total_consumers: number
  api_calls_today: number
  avg_response_time_ms: number
  uptime_pct: number
}

interface API {
  id: string
  name: string
  category: string
  version: string
  consumers: number
  calls_today: number
  status: string
}

interface Category {
  name: string
  apis: number
  consumers: number
}

export default function APIMarketplacePage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [apis, setApis] = useState<API[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, apisRes, categoriesRes] = await Promise.all([
          fetch('/api/apimarketplace/statistics'),
          fetch('/api/apimarketplace/apis'),
          fetch('/api/apimarketplace/categories')
        ])
        setStatistics(await statsRes.json())
        setApis(await apisRes.json())
        setCategories(await categoriesRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-800',
      'Beta': 'bg-blue-100 text-blue-800',
      'Deprecated': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading API Marketplace...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">API Marketplace</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_apis}</div>
              <div className="text-sm text-gray-500">Total APIs</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_apis}</div>
              <div className="text-sm text-gray-500">Active APIs</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.total_consumers.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Total Consumers</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{(statistics.api_calls_today / 1000000).toFixed(1)}M</div>
              <div className="text-sm text-gray-500">Calls Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.avg_response_time_ms}ms</div>
              <div className="text-sm text-gray-500">Avg Response</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.uptime_pct}%</div>
              <div className="text-sm text-gray-500">Uptime</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Available APIs</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">API Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Version</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Consumers</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Calls/Day</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {apis.map((api) => (
                    <tr key={api.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{api.name}</td>
                      <td className="px-4 py-3 text-gray-600">{api.category}</td>
                      <td className="px-4 py-3 text-gray-600">{api.version}</td>
                      <td className="px-4 py-3 text-gray-600">{api.consumers}</td>
                      <td className="px-4 py-3 text-gray-600">{api.calls_today.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(api.status)}`}>
                          {api.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Categories</h2></div>
            <div className="p-4 space-y-3">
              {categories.map((cat, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium">{cat.name}</div>
                  <div className="text-sm text-gray-500">{cat.apis} APIs | {cat.consumers} Consumers</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
